<template>
  <div v-if="showAndScroll()" ref="errorblock" class="form-message-list">
    <el-collapse v-for="(messages, type) in messageList" v-show="messages.length" v-model="activeGroups" :key="type">
      <el-collapse-item
        :title="dynamicHeaders(type, messages)"
        :name="type"
        class="form-error-list"
        :class="dynamicClassSelection(type)"
      >
        <div class="message-container" role="alert">
          <div v-for="(msg, idx) in messages" :key="idx" class="message-list">
            <div class="flex-container d-flex" @click="setFocus(msg)">
              <span class="material-icons" @click="clearError(type, msg)">report_problem</span>
              <p class="alert-message ms-2 d-flex align-items-center">
                <span v-if="msg.label"> {{ $i18n.t(msg.label) }}:&nbsp; </span>
                {{ $i18n.t(getDisplayedErrorMessage(msg)) }}
                <span v-if="msg.pageLabel"> {{ $i18n.t('form_error_page') }}:&nbsp;{{ $i18n.t(msg.pageLabel) }} </span>
              </p>
            </div>
          </div>
        </div>
      </el-collapse-item>
    </el-collapse>
  </div>
</template>

<script>
import notify from '../../utils/mixin-notify'
import { get, find, cloneDeep } from 'lodash'
import parseError from '../../utils/mixin-parse-error-message'

const FIELD_ELEMENT_TARGET_RE = /(field\d+)|(id_\d+(_\d+)*)/gi
const SUBFORM_FIELD_ELEMENT_TARGET_RE = /[a-z0-9]+-[\w_]+\[\d+]/

/**
 * @class Error
 *
 * @description This component renders the errorbag of its parent Form component
 */
export default {
  name: 'error',
  mixins: [notify, parseError],
  props: {
    messageStore: {
      type: Array,
      default: () => [],
    },
    errorPager: {
      type: Boolean,
      default: false,
    },
    popMessages: {
      type: Object,
      default: () => ({}),
    },
    showMessages: {
      type: Boolean,
      default: true,
    },
    inPopup: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      activeGroups: ['error'],
      savedNotifications: [],
    }
  },
  methods: {
    setFocus(error) {
      const node = this.getNode(error)
      if (error.pageLabel) {
        this.activePageId = get(this.form, [ 'pages', error.page, 'id' ], 'page0')
      }
      if (!error.name) return
      if (node) {
        this.$nextTick(() => {
          this.scrollToNode(node)
        })
      }
    },
    parseMessagePage(page) {
      return page ? parseInt(page.replace(/\D/g, '')) : 0 // @regex csak a szamot hagyja meg
    },
    clearError(type, error) {
      this.$dispatch('removeErrorMessage', error)
    },
    saveNotification(type, msg, params, label, page) {
      this.$client.execute({
        operationId: 'Notification_saveNotification',
        parameters: {
          type: type,
          title: label,
          msg,
          params,
          page,
          pushMessage: true,
          idSUser: null,
          idSubsystem: null,
          config: JSON.stringify({
            type,
          }),
        },
        requestContentType: 'application/x-www-form-urlencoded',
      })
    },
    scrollToNode(node) {
      if (node) {
        const tab = node.closest('.tab-pane.card-body:not(.active)')
        if (tab) {
          const id = tab.getAttribute('aria-labelledby')
          const link = document.querySelector(`#${id}`)
          link && link.click()
        }
        this.$nextTick(() => {
          node.scrollIntoView({ behavior: 'smooth' })
          if (this.inPopup) {
            const body = document.querySelector('section.el-drawer__body')
            body.scrollBy(0, -body.scrollTop)
          }
          node.focus()
        })
      }
    },
    getNode(error) {
      const fieldTargetName = error.msg.match(FIELD_ELEMENT_TARGET_RE) || [error.name]
      let node = null
      const selectNode = (selector) =>
        document.querySelector(`[id*=${CSS.escape(this.$get('idForm'))}] [name='${selector}']`)
      if (error.fieldType === 'subform') {
        node = fieldTargetName && selectNode(fieldTargetName[0])
      } else if (SUBFORM_FIELD_ELEMENT_TARGET_RE.test(error.name)) {
        const [subFormId, subFieldName] = error.name.split('-')
        node = document.querySelector(
          `[id*=${CSS.escape(this.$get('idForm'))}] [data-sub-form-id*=${CSS.escape(
            subFormId,
          )}] [name='${subFieldName}']`,
        )
      } else {
        node =
          fieldTargetName &&
          document.querySelector(`[id*=${CSS.escape(this.$get('idForm'))}] [id='${fieldTargetName[0]}-tooltip-target']`)
      }
      if (!node) {
        node = selectNode(error.name)
      }
      return node
    },
    showAndScroll() {
      if (this.showMessages) {
        this.$nextTick(() => {
          this.scrollToNode(this.$refs.errorblock)
        })
      }
      return this.showMessages
    },
    dynamicClassSelection(type) {
      let style
      switch (type) {
        case 'error':
          style = 'danger'
          break
        case 'success':
          style = 'success'
          break
        default:
          style = 'warning'
          break
      }
      return `${style}-box`
    },
    dynamicHeaders(type, message) {
      let header
      switch (type) {
        case 'error':
          header = 'errors'
          break
        case 'success':
          header = get(message, '[0].type', 'success')
          break
        default:
          header = 'warnings'
          break
      }
      return this.$i18n.t(`form_${header}`)
    },
  },
  computed: {
    messageList() {
      const messageList = {
        error: [],
        success: [],
        warning: [],
      }
      let warnings = []
      const sortedMessages = cloneDeep(this.messageStore).sort((a, b) => (a.type === 'success' ? 1 : -1))
      let saveNoti = !find(sortedMessages, { type: 'error' })

      sortedMessages.reduce((acc, { name, type, msg, label, page, pageLabel, fieldType, interpolate }) => {
        name = name ? name : type
        const message = {
          name,
          type,
          msg,
          label,
          pageLabel,
          fieldType,
          interpolate,
        }
        // message: kiszedjuk a parametereket, mezonev, szamok
        const { text, params } = this.createMessageObject({
          msg,
          name,
          label,
          fieldType,
        })
        // msg = { msg, label, number }
        if (page) {
          Object.assign(message, { page: this.parseMessagePage(page) })
        }
        const isMessageDuplicate = (arr) => arr.find((infoMessage) => infoMessage.name === name)
        const notification = {
          title: label,
          text,
          params,
          page,
          type,
          fieldType,
          group: 'basic-notification',
        }

        switch (type) {
          case 'warning':
            if (!isMessageDuplicate(messageList.warning) && !isMessageDuplicate(warnings)) {
              messageList.warning.push(message)
              warnings.push(message)
              // ha ugyanazzal a 'name'-el van noti a régi és az új value-ban is, és ez az aktuális noti, akkor ne mentsük el, mert már el lett mentve!
              if (saveNoti && !find(this.savedNotifications, { type, msg, label })) {
                this.saveNotification(type, text, params, label, page)
                this.savedNotifications.push({ type, msg, label })
              }
            }
            if (this.popMessages.warning) this.showNotification(notification)
            break
          case 'error':
            if (!isMessageDuplicate(messageList.error)) {
              messageList.error.push(message)
            }
            this.$emit('on-error')
            if (this.popMessages.error) this.showNotification(notification)
            break
          case 'success':
            if (!isMessageDuplicate(messageList.success)) {
              messageList.success.push(message)
              if (this.inPopup) {
                this.showNotification(notification)
              } else if (saveNoti) {
                this.saveNotification(type, text, params, label, page)
              }
            }
            if (this.popMessages.success) this.showNotification(notification)
            break
          case 'success-verification':
            if (!isMessageDuplicate(messageList.success)) {
              messageList.success.push(message)
            }
            break
          default:
            if (!isMessageDuplicate(warnings)) {
              messageList.warning.push(message)
              warnings.push(message)
              if (saveNoti) this.saveNotification(type, text, params, label, page)
            }
            if (this.popMessages.warning) this.showNotification(notification)
            break
        }
        return acc
      }, {})
      return messageList
    },
    activePageId: {
      get() {
        return this.$get('activePageId')
      },
      set(activePageId) {
        this.$dispatch('setActivePageId', { activePageId })
      },
    },
    form() {
      return this.$get('form')
    },
  },
}
</script>

<style lang="scss" scoped>
.form-message-list {
  .form-error-list {
    margin-bottom: 20px;
  }
  .message-container .message-list span,
  .flex-container .alert-message {
    cursor: pointer;
  }
}
</style>
